import { load } from 'cheerio'
import type { CheerioAPI } from 'cheerio'
import type { BasicAcceptedElems } from 'cheerio'
import type { AnyNode } from 'domhandler'
import preserveStylingUtilities from 'utils/tinyMCE/updateDynamicText/preserveStylingUtilities'

const { parseCSS, stringifyCSS } = preserveStylingUtilities()

export const extractExistingTableCellStyles = (
  element: BasicAcceptedElems<AnyNode>,
): string[] => {
  const $ = load('')
  const $element = $(element)
  const perCellStyles: string[] = []

  $element.find('td').each(function () {
    const spans = $(this).find('span')
    const merged = parseCSS(null)

    spans.each(function () {
      const style = $(this).attr('style') || ''
      if (style) Object.assign(merged, parseCSS(style))
    })

    perCellStyles.push(stringifyCSS(merged))
  })

  const styledCells = perCellStyles.filter((s) => s)
  if (styledCells.length === 1) {
    const sourceStyle = styledCells[0]
    return perCellStyles.map((s) => s || sourceStyle)
  }

  return perCellStyles
}

export const restoreTableCellStyles = (
  tableElement: CheerioAPI,
  perCellStyles: string[],
): void => {
  if (!perCellStyles || perCellStyles.length === 0) return

  // Representative style used for appended cells (newly-added pallbearers) that
  // have no matching perCellStyles entry. Falls back to the last non-empty
  // saved style so a new cell renders identically to the existing ones.
  const fallbackStyle = [...perCellStyles].reverse().find((s) => s) || ''

  let cellIndex = 0
  tableElement('td').each((_, td) => {
    // For appended cells beyond the saved entries, reuse the representative
    // style instead of leaving them unstyled.
    const savedStyle =
      cellIndex < perCellStyles.length
        ? perCellStyles[cellIndex]
        : fallbackStyle
    if (savedStyle) {
      const span = tableElement(td).find('span').last()
      if (span.length > 0) {
        span.attr('style', savedStyle)
      }
    }
    cellIndex++
  })
}

export const preserveTableDivStyling = (
  initialElement: BasicAcceptedElems<AnyNode>,
  tableElement: CheerioAPI,
): void => {
  const $ = load('')
  const $element = $(initialElement)

  const existingStyle = $element.attr('style') || null
  const savedStyle = $element.attr('data-style') || null
  const persistentFontSize = $element.attr('data-persistent-font-size') || null

  const styling = existingStyle || savedStyle
  if (!styling) return

  const targetDiv = tableElement('div')
  if (targetDiv.length === 0) return

  let finalStyle = styling
  if (persistentFontSize) {
    finalStyle = styling
      .split(';')
      .filter((s) => !s.trim().startsWith('font-size'))
      .concat(`font-size: ${persistentFontSize}`)
      .join(';')
    targetDiv.attr('data-persistent-font-size', persistentFontSize)
  }

  targetDiv.attr('style', finalStyle).attr('data-style', finalStyle)
}
